import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { appDataSelector } from 'app/selectors';
import { useStepperContext } from '../Context';

import { Box, TextField } from '@material-ui/core';
import { FormikInput } from 'app/components/FormikInput';
import { DocumentDtInterface, TechnicalVisitPageState } from '../types';
import { useFormikContext } from 'formik';
import _ from 'lodash';
import { GroupSelect } from 'app/components/GroupSelect';
import { useLocation } from 'react-router-dom';

export const Form = () => {
  const { pathname } = useLocation();

  const { setFieldValue, values } = useFormikContext<DocumentDtInterface>();
  const { t } = useTranslation();
  const { setValidationSchema, variant } = useStepperContext();
  const {
    onSmallDevice,
    lists,
    isSupplier,
    technicalVisitsCached,
  } = useSelector(appDataSelector);
  const [listsOptions, setListsOptions] = React.useState<any>({});

  const [listKeys] = React.useState([
    'additional_three-phase',
    'triphasé_dt',
    'single_phase',
    'additional_mono-phase',
  ]);
  React.useEffect(() => {
    setListsOptions(
      lists
        .filter((l) => listKeys.includes(l.key))
        .map((l) => ({
          header: l.name,
          subLists: l.sublists?.map((cl) => ({
            value: cl.id,
            displayedValue: cl.name,
          })),
        })),
    );
  }, [setListsOptions, lists, listKeys]);

  React.useEffect(
    () =>
      setValidationSchema(
        yup.object({
          oneline: yup.number().nullable(),
          emission: yup
            .number()
            .nullable()
            .min(0, t(translations.formErrors.minLength, { length: '0' }))
            .typeError(t(translations.formErrors.onlyDigits)),
          furtherInformation: yup.string().nullable(),
        }),
      ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const technicalVisitId = React.useMemo(() => {
    const id = pathname.split('/').slice(-1)[0];
    return id;
  }, [pathname]);

  const item: TechnicalVisitPageState = React.useMemo(
    () => technicalVisitsCached[technicalVisitId],
    [technicalVisitsCached, technicalVisitId],
  );

  return (
    <Box display="flex" flexDirection="column">
      {Object.keys(values).length > 0 && values.hasOwnProperty('oneline') && (
        <>
          <Box>
            <Box
              display="flex"
              flexDirection={onSmallDevice ? 'column' : 'row'}
              flexWrap="wrap"
            >
              {!_.isEmpty(listsOptions) &&
                item.customer.attributedTo !== 'novita' && (
                  <FormikInput
                    name="oneline"
                    label={t(translations.DocumentDtForms.labels.oneline)}
                    extraAttr={{
                      ...variant,
                      disabled: isSupplier,
                      options: listsOptions || [],
                      style: { width: '100%', minWidth: '100px' },
                    }}
                    containerStyle={{ flex: '3' }}
                    component={GroupSelect}
                  />
                )}
              <FormikInput
                name="emission"
                label={t(translations.DocumentDtForms.labels.emission)}
                extraAttr={{
                  ...variant,
                  disabled: isSupplier,
                  style: { width: '100%', minWidth: '150px' },
                  onChange: (e) =>
                    setFieldValue(
                      'emission',
                      e.target.value
                        ? e.target.value.replaceAll(',', '.')
                        : null,
                    ),
                }}
                containerStyle={{ flex: '1' }}
                component={TextField}
              />
            </Box>
          </Box>
          <Box>
            <FormikInput
              name="furtherInformation"
              label={t(translations.DocumentDtForms.labels.furtherInformation)}
              extraAttr={{
                ...variant,
                disabled: isSupplier,
                rows: 6,
                multiline: true,
                style: { width: '100%' },
              }}
              component={TextField}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
